
import React, { useState, useEffect } from 'react';
import {
  User,
  Mail,
  Lock,
  MapPin,
  Music,
  Link as LinkIcon,
  DollarSign,
  Briefcase,
  Search,
  BrainCircuit,
  Loader2,
  CheckCircle,
  Navigation,
} from 'lucide-react';
import { checkContentForViolation } from '../services/moderationService';
import { searchArtist } from '../services/musicBrainzService';
import { Artist } from '../data/knowledgeGraphSchema';
import { API_BASE_URL } from '@/config';
import { UserRole } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

interface ArtistFormProps {
  onComplete: () => void;
  onBlockUser: () => void;
  role: UserRole;
  walletAddress?: string;
  initialData?: Artist;
}

const ArtistForm: React.FC<ArtistFormProps> = ({ onComplete, onBlockUser, role, walletAddress, initialData }) => {
  const { isNewWeb3User } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    stageName: initialData?.name || '',
    location: '',
    genres: initialData?.genres?.join(', ') || '',
    bio: initialData?.bio || '',
    portfolioUrl: '',
    ratePerGig: '',
    skills: '',
    equipment: '',
    musicBrainzId: initialData?.id || '',
    walletAddress: walletAddress || '',
  });

  const [musicBrainzSearch, setMusicBrainzSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Artist[]>([]);
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(initialData || null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    setFormData(prev => ({ ...prev, walletAddress: walletAddress || '' }));
  }, [walletAddress]);

  const handleAutoDetectLocation = () => {
    if (!navigator.geolocation) {
      console.error('Geolocation is not supported by your browser.');
      return;
    }
    setIsDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const locationString = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
          setFormData((prev) => ({ ...prev, location: locationString }));
        } catch {
          console.error('Could not fetch city and country from coordinates.');
        } finally {
          setIsDetectingLocation(false);
        }
      },
      () => {
        console.error('Unable to retrieve your location. Please check your browser permissions.');
        setIsDetectingLocation(false);
      }
    );
  };

  const handleSearch = async () => {
    if (!musicBrainzSearch) return;
    const results = await searchArtist(musicBrainzSearch);
    setSearchResults(results);
    if (results.length === 0) {
      console.warn(`No artists found on MusicBrainz matching "${musicBrainzSearch}".`);
    }
  };

  const handleSelectArtist = (artist: Artist) => {
    setSelectedArtist(artist);
    setFormData({
      ...formData,
      stageName: artist.name,
      bio: artist.bio || formData.bio,
      musicBrainzId: artist.id,
      genres: artist.genres?.join(', ') || formData.genres,
    });
    setSearchResults([]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setIsSubmitting(true);

    const requiredFields: (keyof typeof formData)[] = ['firstName', 'lastName', 'email', 'password', 'stageName', 'location', 'genres', 'bio'];

    const missingFields = requiredFields.filter(field => !formData[field]);
    if (missingFields.length > 0) {
      setSubmitError(`Please fill out all required fields: ${missingFields.join(', ')}.`);
      setIsSubmitting(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitError('Please enter a valid email address.');
      setIsSubmitting(false);
      return;
    }
    if (formData.password.length < 8) {
      setSubmitError('Password must be at least 8 characters long.');
      setIsSubmitting(false);
      return;
    }

    if (checkContentForViolation(Object.values(formData).join(' '))) {
      onBlockUser();
      setIsSubmitting(false);
      return;
    }

    // Standardize the payload to match other forms
    const nestedFormData = {
        stageName: formData.stageName,
        location: formData.location,
        genres: formData.genres.split(',').map(s => s.trim()).filter(Boolean),
        bio: formData.bio,
        portfolioUrl: formData.portfolioUrl,
        ratePerGig: formData.ratePerGig ? parseInt(formData.ratePerGig, 10) : null,
        skills: formData.skills.split(',').map(s => s.trim()).filter(Boolean),
        equipment: formData.equipment.split(',').map(s => s.trim()).filter(Boolean),
        musicBrainzId: selectedArtist?.id || null,
    };

    const finalPayload = {
      name: `${formData.firstName} ${formData.lastName}`.trim(),
      email: formData.email,
      password: formData.password,
      role: role,
      walletAddress: formData.walletAddress || null,
      formData: nestedFormData,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/join-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(finalPayload),
      });

      if (!response.ok) {
        // Try to parse the error response as JSON, but fall back to text if it fails
        const responseText = await response.text();
        let errorData = { error: 'Failed to submit application.' };
        try {
          errorData = JSON.parse(responseText);
        } catch (e) {
          console.error("Could not parse error response as JSON:", responseText);
          errorData.error = responseText.substring(0, 100); // Show first 100 chars as a fallback
        }
        throw new Error(errorData.error || 'Failed to submit application.');
      }

      setFormSubmitted(true);

    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unknown error occurred.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (formSubmitted) {
    return (
      <div className="text-center py-10">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-white">Application Submitted!</h3>
        <p className="text-kala-300 mt-2">Thank you for your interest. An admin will review your details shortly.</p>
        <button onClick={onComplete} className="mt-6 bg-kala-secondary text-kala-900 font-bold py-2 px-4 rounded-lg">Finish</button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-8">
      {!selectedArtist && (
        <div>
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <BrainCircuit className="text-kala-secondary w-5 h-5" /> Knowledge Graph Link (Optional)
          </h3>
          <p className="text-sm text-kala-300 mb-2">
            Bootstrap your profile by linking to a MusicBrainz artist entry.
          </p>
          <div className="flex gap-2">
            <input
              value={musicBrainzSearch}
              onChange={(e) => setMusicBrainzSearch(e.target.value)}
              className="w-full bg-kala-900 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary"
              placeholder="Search for your artist name on MusicBrainz..."
            />
            <button
              type="button"
              onClick={handleSearch}
              disabled={isSubmitting}
              className="bg-kala-secondary text-kala-900 font-bold py-2 px-4 rounded-lg hover:bg-cyan-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Search className="w-5 h-5" /> Search
            </button>
          </div>
          {searchResults.length > 0 && (
            <div className="mt-4 bg-kala-900/50 border border-kala-700 rounded-lg p-4 max-h-60 overflow-y-auto">
              <h4 className="text-white font-bold mb-2">Select your artist profile:</h4>
              <ul>
                {searchResults.map((artist) => (
                  <li key={artist.id}>
                    <button
                      type="button"
                      onClick={() => handleSelectArtist(artist)}
                      className="w-full text-left p-2 hover:bg-kala-700 rounded-md transition-colors"
                    >
                      <p className="font-bold">{artist.name}</p>
                      <p className="text-sm text-kala-400 line-clamp-2">{artist.bio}</p>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {selectedArtist && (
        <div className="bg-green-900/20 border border-green-500/30 rounded-xl p-3 flex items-center gap-3">
          <BrainCircuit className="w-5 h-5 text-green-400 shrink-0" />
          <p className="text-sm text-green-200">
            Profile linked to MusicBrainz artist:{' '}
            <span className="font-bold">{selectedArtist.name}</span>
          </p>
        </div>
      )}

      <div>
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <User className="text-kala-secondary w-5 h-5" /> Legal Name & Contact
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">First Name</label>
            <input name="firstName" onChange={handleChange} value={formData.firstName} className="w-full bg-kala-900 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" placeholder="Jane" />
          </div>
          <div>
            <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">Last Name</label>
            <input name="lastName" onChange={handleChange} value={formData.lastName} className="w-full bg-kala-900 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" placeholder="Doe" />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
              <input type="email" name="email" onChange={handleChange} value={formData.email} className="w-full bg-kala-900 border border-kala-700 rounded-lg pl-10 pr-4 py-2 text-white outline-none focus:border-kala-secondary" placeholder="jane@example.com" />
            </div>
            <p className="text-[10px] text-kala-500 mt-1">This will be used for notifications and to log in.</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
              <input type="password" name="password" onChange={handleChange} value={formData.password} className="w-full bg-kala-900 border border-kala-700 rounded-lg pl-10 pr-4 py-2 text-white outline-none focus:border-kala-secondary" placeholder="8+ characters" />
            </div>
             <p className="text-[10px] text-kala-500 mt-1">Create a secure password for your account.</p>
          </div>
        </div>
      </div>

      <div className="border-t border-kala-700/50"></div>
      
      <div>
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Music className="text-purple-400 w-5 h-5" /> Creative Profile
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">Artist / Stage Name</label>
            <input name="stageName" onChange={handleChange} value={formData.stageName} className="w-full bg-kala-900 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" placeholder="e.g. Neon Pulse" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">Location</label>
            <div className="flex items-center gap-2">
              <div className="relative w-full">
                <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
                <input name="location" onChange={handleChange} value={formData.location} className="w-full bg-kala-900 border border-kala-700 rounded-lg pl-10 pr-4 py-2 text-white outline-none focus:border-kala-secondary" placeholder="City, Country or Lat, Long" />
              </div>
              <button
                type="button"
                onClick={handleAutoDetectLocation}
                disabled={isDetectingLocation}
                className="bg-kala-700 p-2.5 rounded-lg hover:bg-kala-600 transition-colors disabled:opacity-50"
              >
                {isDetectingLocation ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">Genres</label>
            <input name="genres" onChange={handleChange} value={formData.genres} className="w-full bg-kala-900 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" placeholder="Techno, Ambient, Jazz" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">Bio</label>
            <textarea name="bio" rows={4} onChange={handleChange} value={formData.bio} maxLength={1000} className="w-full bg-kala-900 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary resize-none" placeholder="Tell us about your artistic journey..." />
          </div>
        </div>
      </div>

      <div className="border-t border-kala-700/50"></div>

      <div>
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Briefcase className="text-green-400 w-5 h-5" /> Professional Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">Portfolio / Press Kit URL</label>
            <div className="relative">
              <LinkIcon className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
              <input name="portfolioUrl" onChange={handleChange} value={formData.portfolioUrl} className="w-full bg-kala-900 border border-kala-700 rounded-lg pl-10 pr-4 py-2 text-white outline-none focus:border-kala-secondary" placeholder="https://..." />
            </div>
          </div>
          <div>
            <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">Standard Rate (Starting)</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
              <input name="ratePerGig" type="number" onChange={handleChange} value={formData.ratePerGig} className="w-full bg-kala-900 border border-kala-700 rounded-lg pl-10 pr-4 py-2 text-white outline-none focus:border-kala-secondary" placeholder="500" />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">Skills / Assets</label>
            <input name="skills" onChange={handleChange} value={formData.skills} className="w-full bg-kala-900 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" placeholder="e.g. Sound Design, Piano, Logic Pro X" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">Equipment List (Available for Rent/Use)</label>
            <input name="equipment" onChange={handleChange} value={formData.equipment} className="w-full bg-kala-900 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" placeholder="e.g. Fender Strat, Pioneer CDJs" />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">Wallet Address</label>
            <div className="relative">
              <input
                name="walletAddress"
                onChange={handleChange}
                value={formData.walletAddress}
                className={`w-full bg-kala-900 border rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary ${
                  walletAddress && formData.walletAddress === walletAddress
                    ? 'border-green-500/50'
                    : 'border-kala-700'
                }`}
                placeholder="0x... or connect wallet for auto-fill"
              />
              {walletAddress && formData.walletAddress === walletAddress && (
                  <CheckCircle className="absolute right-3 top-2.5 w-5 h-5 text-green-500" title="Wallet address matches connected wallet"/>
              )}
            </div>
            <p className="text-[10px] text-kala-500 mt-1">
              {isNewWeb3User
                ? 'Your connected wallet is auto-filled. You can override it if needed.'
                : 'Optional: Add a wallet address for crypto payments.'}
            </p>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={isSubmitting}
        className="w-full bg-kala-secondary text-kala-900 font-bold py-4 rounded-xl hover:bg-cyan-400 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-cyan-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? <Loader2 className="w-6 h-6 animate-spin" /> : 'Submit Application'}
      </button>

      {submitError && <p className="text-red-500 mt-4 text-center">{submitError}</p>}
    </div>
  );
};

export default ArtistForm;
